import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { Button } from '@components';
import type { TabItem } from '@types';
import { cn } from '@utils';

interface TabButtonProps {
  tab: TabItem;
  isActive: boolean;
  onClick: (tabKey: string) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  animated?: boolean;
}

const sizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
} as const;

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
} as const;

const motionVariants = {
  initial: {
    scale: 1,
    opacity: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
      duration: 0.15,
    },
  },
  tap: {
    scale: 0.98,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 30,
      duration: 0.1,
    },
  },
  disabled: {
    scale: 1,
    opacity: 0.5,
    y: 0,
  },
};

const contentVariants = {
  initial: { opacity: 1 },
  hover: {
    opacity: 1,
    transition: { duration: 0.15 },
  },
  active: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
};

export const TabButton: React.FC<TabButtonProps> = ({
  tab,
  isActive,
  onClick,
  size = 'md',
  className,
  animated = true,
}) => {
  const handleClick = (): void => {
    if (!tab.disabled) {
      onClick(tab.key);
    }
  };

  const dynamicButtonClasses = cn(
    'rounded-lg font-normal transition-all duration-300 ease-in-out focus:outline-0 relative overflow-hidden enabled:shadow-none',
    sizeClasses[size],
    !isActive && !tab.disabled && 'hover:text-theme-primary-main bg-transparent text-gray-400',
    tab.disabled && 'opacity-50 cursor-not-allowed',
    className,
  );

  const ButtonContent: React.FC = () => (
    <motion.div
      className="flex items-center gap-2 relative z-10"
      variants={contentVariants}
      initial="initial"
      animate={isActive ? 'active' : 'initial'}
      whileHover={!tab.disabled ? 'hover' : 'initial'}
    >
      <AnimatePresence mode="wait">
        {tab.icon && (
          <motion.span
            className="flex-shrink-0"
            initial={{ opacity: 0, scale: 0.8, x: -5 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: -5 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
              duration: 0.2,
            }}
          >
            {typeof tab.icon === 'function' ? <tab.icon size={iconSizes[size]} className="text-current" /> : tab.icon}
          </motion.span>
        )}
      </AnimatePresence>

      <motion.span
        className="whitespace-nowrap select-none"
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {tab.name}
      </motion.span>

      <AnimatePresence>
        {tab.badge && (
          <motion.span
            className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-lg min-w-[1.25rem] h-5 flex items-center justify-center font-semibold shadow-sm"
            initial={{ opacity: 0, scale: 0, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0, x: -10 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
              duration: 0.25,
              delay: 0.1,
            }}
            whileHover={{
              scale: 1.1,
              transition: { duration: 0.15 },
            }}
          >
            {tab.badge}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // Enhanced ripple effect for active state
  const RippleEffect: React.FC = () => (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-theme-primary-main/10 to-theme-primary-main/5 rounded-lg"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            duration: 0.4,
          }}
        />
      )}
    </AnimatePresence>
  );

  if (!animated) {
    return (
      <Button
        onClick={handleClick}
        className={dynamicButtonClasses}
        disabled={tab.disabled}
        aria-current={isActive ? 'page' : undefined}
        role="tab"
        aria-selected={isActive}
        data-key={tab.key}
      >
        <div className="flex items-center gap-2">
          {tab.icon && (
            <span className="flex-shrink-0">
              {typeof tab.icon === 'function' ? <tab.icon size={iconSizes[size]} className="text-current" /> : tab.icon}
            </span>
          )}
          <span className="whitespace-nowrap">{tab.name}</span>
          {tab.badge && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-lg min-w-[1.25rem] h-5 flex items-center justify-center font-semibold">
              {tab.badge}
            </span>
          )}
        </div>
      </Button>
    );
  }

  return (
    <motion.div
      className="relative"
      initial="initial"
      whileHover={!tab.disabled ? 'hover' : 'disabled'}
      whileTap={!tab.disabled ? 'tap' : 'disabled'}
      animate={tab.disabled ? 'disabled' : 'initial'}
      variants={motionVariants}
    >
      <RippleEffect />
      <Button
        onClick={handleClick}
        className={dynamicButtonClasses}
        disabled={tab.disabled}
        aria-current={isActive ? 'page' : undefined}
        role="tab"
        aria-selected={isActive}
        data-key={tab.key}
      >
        <ButtonContent />
      </Button>
    </motion.div>
  );
};
